// app/components/privacy-graph-scrolly/index.tsx

import { useEffect, useRef, useState } from "react";
import { LAYOUT, MOBILE } from "./graph-layout";
import { KEYFRAME_BANDS, MOBILE_KEYFRAME_BANDS } from "./keyframe-data";
import { useStickyController } from "./sticky-controller";
import { ScrollyBanner } from "./scrolly-banner";
import { MotionModeCanvas } from "./motion-mode-canvas";
import { MobileVerticalGraph } from "./mobile-vertical-graph";
import { SVG_COLORS } from "@/lib/svg-colors";

const STICKY_OFFSET_PX = 104;

export function PrivacyGraphScrolly() {
  const outerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isMobileGraph, setIsMobileGraph] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 1023px)");
    const update = () => setIsMobileGraph(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  const activeBands = isMobileGraph ? MOBILE_KEYFRAME_BANDS : KEYFRAME_BANDS;

  const { scrollYProgress, hydrated } = useStickyController(
    outerRef,
    activeBands,
  );

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { el.style.willChange = entry?.isIntersecting ? "transform" : "auto"; },
      { rootMargin: "200px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const root = outerRef.current?.closest("[data-scrolly-root]");
    if (root) root.setAttribute("data-scrolly-active", "true");
    return () => { if (root) root.setAttribute("data-scrolly-active", "false"); };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      return;
    }
    const node = outerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        node.classList.toggle("scrolly-in-view", entry.isIntersecting);
      },
      { rootMargin: "-10% 0px", threshold: 0 },
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={outerRef}
      className="relative block h-[300vh] lg:h-[450vh]"
    >
      <div
        ref={sectionRef}
        className="sticky top-[6.5rem] flex h-[calc(100svh-6.5rem)] flex-col items-center lg:h-[calc(100vh-6.5rem)]"
      >
        <ScrollyBanner
          outerRef={outerRef}
          progress={scrollYProgress}
          bands={activeBands}
          stickyOffsetPx={STICKY_OFFSET_PX}
        />
        <div className="bg-dot-grid-16 relative mx-auto flex w-full max-w-[620px] min-h-0 flex-1 items-center justify-center lg:max-w-[1600px]">
          <svg
            viewBox={`0 0 ${MOBILE.viewBox.w} ${MOBILE.viewBox.h}`}
            width="100%"
            className="max-h-full max-w-[620px] select-none lg:hidden"
            preserveAspectRatio="xMidYMid meet"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="Your AWS account becoming a vertical privacy graph: scan, emerge, drift, findings"
          >
            <defs>
              <linearGradient id="mobile-scan-sweep" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="rgba(47,107,99,0)" />
                <stop offset="50%" stopColor="rgba(47,107,99,0.26)" />
                <stop offset="100%" stopColor="rgba(47,107,99,0)" />
              </linearGradient>
              <marker id="arrow-flow-mobile" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={8} markerHeight={8} orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 Z" fill="rgba(74,83,97,0.55)" />
              </marker>
              <marker id="arrow-flow-lit-mobile" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={8} markerHeight={8} orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 Z" fill={SVG_COLORS.drift} />
              </marker>
              <marker id="arrow-drift-mobile" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={8} markerHeight={8} orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 Z" fill={SVG_COLORS.risk} />
              </marker>
            </defs>
            <MobileVerticalGraph progress={scrollYProgress} bands={activeBands} />
          </svg>
          <svg
            viewBox={`0 0 ${LAYOUT.viewBox.w} ${LAYOUT.viewBox.h}`}
            width="100%"
            className="hidden max-h-full max-w-[1600px] select-none lg:block"
            preserveAspectRatio="xMidYMid meet"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="Your AWS account becoming a privacy graph: scan, emerge, drift, findings"
          >
            <defs>
              <marker id="arrow-flow" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={8} markerHeight={8} orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 Z" fill="rgba(74,83,97,0.55)" />
              </marker>
              <marker id="arrow-flow-lit" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={8} markerHeight={8} orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 Z" fill={SVG_COLORS.drift} />
              </marker>
              <marker id="arrow-drift" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={8} markerHeight={8} orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 Z" fill={SVG_COLORS.risk} />
              </marker>
            </defs>
            <MotionModeCanvas progress={scrollYProgress} hydrated={hydrated} />
          </svg>
        </div>
      </div>
    </div>
  );
}
